"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="relative bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent px-0 py-0 h-auto">
              <Link to={item.href || "#"} className="group text-foreground text-sm font-default font-medium tracking-tight transition-colors duration-200 hover:text-secondary focus:text-secondary">
                <span className="relative">
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--secondary)] transition-all duration-300 ease-out group-hover:w-full" aria-hidden="true" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col px-6 pt-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-[var(--border)] py-4">
          <SheetClose asChild>
            <Link to={item.href || "#"} onClick={onNavigate} className="block text-foreground text-lg font-default font-medium tracking-tight transition-colors hover:text-secondary">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1581" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-[250ms] ease-out ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-[var(--border)]" : "border-b border-transparent"}`}>
      <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-8 md:px-12 lg:px-20">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center" aria-label="Summertime Lane Investments home">
            <WvcLogo className="h-10 w-auto" />
          </Link>

          <span className="hidden lg:inline-flex items-center font-mono text-[0.6875rem] tracking-[0.16em] uppercase text-[var(--muted-foreground)] pl-6 border-l border-[var(--border)]" aria-hidden="true">
            <span className="text-[var(--secondary)] mr-1.5">+</span>
            EST. SLI 2024
          </span>
        </div>

        <div className="hidden md:flex items-center gap-10">
          <MenuProvider menu_id="18">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Button asChild className="rounded-md px-6 py-2.5 h-auto text-sm font-default font-medium tracking-tight bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200">
            <Link to="/HomePage">Inquire</Link>
          </Button>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <Button asChild size="sm" className="rounded-md px-4 py-2 h-auto text-xs font-default font-medium tracking-tight bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/HomePage">Inquire</Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-[var(--accent)] hover:text-foreground" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l border-[var(--border)] p-0 w-[85%] sm:max-w-sm">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-[var(--border)]">
                <SheetTitle className="font-mono text-[0.75rem] tracking-[0.16em] uppercase text-[var(--muted-foreground)] text-left font-normal">
                  <span className="text-[var(--secondary)] mr-1.5">+</span>
                  /navigation
                </SheetTitle>
              </SheetHeader>

              <MenuProvider menu_id="18">
                <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
              </MenuProvider>

              <div className="mt-auto px-6 pb-8 pt-6 border-t border-[var(--border)]">
                <SheetClose asChild>
                  <Button asChild className="w-full rounded-md py-3 h-auto text-sm font-default font-medium bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/HomePage">Inquire</Link>
                  </Button>
                </SheetClose>
                <p className="mt-4 font-mono text-[0.75rem] tracking-[0.08em] text-[var(--muted-foreground)]">
                  Summertime Lane Investments
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}